import React, { useState, useEffect } from 'react';
import { Box, Paper, List, ListItem, ListItemButton, ListItemText, ListItemIcon, Typography, Divider, Badge } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// Icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CampaignIcon from '@mui/icons-material/Campaign';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountTreeIcon from '@mui/icons-material/AccountTree'; 
import AssignmentIcon from '@mui/icons-material/Assignment';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';

// Components
import GeneralSection from './GeneralSection';
import PunctualitySection from './PunctualitySection';
import AttendanceReportSection from './AttendanceReportSection'; 
import OrgChartSection from './OrgChartSection'; 
import CommunicationCenter from './CommunicationCenter';
import PendingActionsSection from './PendingActionsSection';
import api from '../../api';

const Dashboard = () => {
  const theme = useTheme();
  const { dashboard } = theme;
  
  const [activeMenu, setActiveMenu] = useState('general');
  const [unread, setUnread] = useState({ news: false, announcements: false, notifications: false });
  const [hasPendingActions, setHasPendingActions] = useState(false);
  const empId = localStorage.getItem('emp_id');

  useEffect(() => {
    fetchBackgroundData();
    const interval = setInterval(fetchBackgroundData, 300000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBackgroundData = async () => {
    try {
      const commRes = await api.get(`/communications/unread-status?emp_id=${empId}`);
      setUnread(commRes.data);
      
      const actionRes = await api.get(`/pending-actions/status?emp_id=${empId}`);
      setHasPendingActions(actionRes.data.has_pending);
    } catch (err) {
      console.error("Failed to fetch background status data", err);
    }
  };

  const handleMenuClick = (id) => {
    setActiveMenu(id);
    fetchBackgroundData();
  };

  // NavGroupHeader - NOW USING THEME STYLES
  const NavGroupHeader = ({ title }) => (
    <Typography 
      variant="caption" 
      sx={{ 
        px: dashboard.sectionHeading.paddingX,
        py: dashboard.sectionHeading.paddingY,
        display: 'block', 
        color: dashboard.sectionHeading.color,
        fontWeight: dashboard.sectionHeading.fontWeight,
        mt: dashboard.sectionHeading.marginTop,
        fontSize: dashboard.sectionHeading.fontSize,
        letterSpacing: dashboard.sectionHeading.letterSpacing,
        textTransform: dashboard.sectionHeading.textTransform,
      }}
    >
      {title}
    </Typography>
  );

  // NavItem - USING THEME STYLES
  const NavItem = ({ id, label, icon, hasUnread = false, disabled = false, subtitle = "", customBadge = false }) => (
    <ListItem disablePadding sx={{ mb: dashboard.navItem.marginBottom }}>
      <ListItemButton 
        selected={activeMenu === id} 
        onClick={() => !disabled && handleMenuClick(id)}
        disabled={disabled}
        sx={{
          borderRadius: dashboard.navItem.borderRadius,
          mx: dashboard.navItem.paddingX,
          py: dashboard.navItem.paddingY,
          '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } },
          '&.Mui-selected .MuiListItemIcon-root': { color: 'white' },
          '&.Mui-disabled': { opacity: 0.6 }
        }}
      >
        <ListItemIcon sx={{ 
          minWidth: dashboard.navItem.iconMinWidth, 
          color: activeMenu === id ? 'white' : 'inherit' 
        }}>
          {customBadge ? icon : (
            <Badge color="error" variant="dot" invisible={!hasUnread}>
              {icon}
            </Badge>
          )}
        </ListItemIcon>
        <ListItemText 
          primary={label} 
          secondary={subtitle}
          primaryTypographyProps={{ 
            fontWeight: activeMenu === id ? 'bold' : 'medium', 
            fontSize: dashboard.navItem.primaryFontSize
          }} 
          secondaryTypographyProps={{ 
            fontSize: dashboard.navItem.secondaryFontSize 
          }}
        />
      </ListItemButton>
    </ListItem>
  );

  return (
    <Paper elevation={2} sx={{ 
      display: 'flex', 
      height: dashboard.paper.minHeight,
      width: '100%', 
      borderRadius: dashboard.paper.borderRadius, 
      overflow: 'hidden',
      mt: dashboard.paper.marginTop
    }}>
      
      {/* LEFT SIDEBAR - Independent scrolling */}
      <Box sx={{ 
        width: dashboard.sidebar.width,
        minWidth: dashboard.sidebar.width,
        borderRight: 1, 
        borderColor: 'divider', 
        bgcolor: '#ffffff', 
        pt: dashboard.sidebar.paddingTop,
        pb: dashboard.sidebar.paddingBottom,
        px: dashboard.sidebar.paddingX,
        boxShadow: '1px 0 3px rgba(0,0,0,0.04)',
        zIndex: 1, 
        overflowY: 'auto',
        overflowX: 'hidden',
        height: '100%',
        '&::-webkit-scrollbar': {
          width: 4,
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: 2,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      }}>
        <List component="nav" dense sx={{ py: 0 }}>
          
          <NavGroupHeader title="DASHBOARD" />
          <NavItem id="general" label="General" icon={<DashboardIcon fontSize="small" />} />
          <NavItem id="punctuality" label="Punctuality" icon={<AccessTimeIcon fontSize="small" />} />
          <NavItem id="report" label="Attendance Report" icon={<AssessmentIcon fontSize="small" />} />
          <NavItem 
            id="pending_actions" 
            label="Pending Actions" 
            customBadge={true}
            icon={hasPendingActions ? <NotificationsActiveIcon color="error" fontSize="small" /> : <AssignmentTurnedInIcon fontSize="small" />} 
          />
          
          <Divider sx={{ my: dashboard.divider.marginY, mx: dashboard.divider.marginX }} />

          <NavGroupHeader title="MESSAGES" />
          <NavItem id="news" label="News" icon={<CampaignIcon fontSize="small" />} hasUnread={unread.news} />
          <NavItem id="announcements" label="Announcements" icon={<AnnouncementIcon fontSize="small" />} hasUnread={unread.announcements} />
          <NavItem id="notifications" label="Notifications" icon={<NotificationsIcon fontSize="small" />} hasUnread={unread.notifications} />

          <Divider sx={{ my: dashboard.divider.marginY, mx: dashboard.divider.marginX }} />

          <NavGroupHeader title="ORGANIZATION" />
          <NavItem id="orgchart" label="Organization Structure" icon={<AccountTreeIcon fontSize="small" />} /> 
          <NavItem id="kpa" label="KPA" subtitle="(Coming Soon)" icon={<AssignmentIcon fontSize="small" />} disabled={true} />
          <NavItem id="kra" label="KRA" subtitle="(Coming Soon)" icon={<AssignmentIcon fontSize="small" />} disabled={true} />

        </List>
      </Box>

      {/* RIGHT CONTENT AREA - Independent scrolling */}
      <Box sx={{ 
        flexGrow: 1, 
        p: dashboard.content.padding,
        width: '100%', 
        bgcolor: '#fbfbfb', 
        overflowY: 'auto',
        overflowX: 'hidden',
        height: '100%',
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: 3,
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: 3,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      }}>
        {activeMenu === 'general' && <GeneralSection />}
        {activeMenu === 'punctuality' && <PunctualitySection />}
        {activeMenu === 'report' && <AttendanceReportSection />} 
        {activeMenu === 'pending_actions' && <PendingActionsSection />} 
        
        {activeMenu === 'news' && <CommunicationCenter channel="news" title="Company News" />}
        {activeMenu === 'announcements' && <CommunicationCenter channel="announcements" title="Company Announcements" />}
        {activeMenu === 'notifications' && <CommunicationCenter channel="notifications" title="System Notifications" />}

        {activeMenu === 'orgchart' && <OrgChartSection />} 
      </Box>

    </Paper>
  );
};

export default Dashboard;